/**
 * Мастер «Загрузить парк техники списком» — AK-804.
 *
 * Макет утверждён founder 06.09:
 * `docs/superpowers/prototypes/machinery-forms/04-import-from-file.html`.
 *
 * Три шага: файл → проверка → готово. Главное обещание проверки — пока не
 * нажата «Применить», в парк не записано НИЧЕГО; строка с ошибкой честно
 * названа номером строки файла и остальные её не ждут.
 *
 * Правила («сколько заведём», «что на кнопке», «что в итоге») живут в
 * `lib/machinery/parkImport.ts` — здесь только разметка и состояние.
 */
import { useMemo, useState } from 'react';
import { Check, Download, FileSpreadsheet, Search, Upload } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Stack } from '@/components/ui/stack';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from '@/lib/toast';
import { cn } from '@/lib/utils';
import {
  IMPORT_MAX_MB,
  IMPORT_MAX_ROWS,
  STATUS_TITLES,
  applyButtonLabel,
  doneSummary,
  downloadParkTemplate,
  importErrorText,
  noIdsCount,
  plannedCount,
  useApplyParkImport,
  usePreviewParkImport,
  withChosenModel,
  type ApplyResult,
  type ImportRow,
  type ImportRowStatus,
} from '@/lib/machinery/parkImport';
import { useTextFilterDraft } from '@/lib/filters/useTextFilterDraft';
import { useMachineryCatalog } from './queries';

type Step = 'file' | 'preview' | 'done';

//: Тон чипа статуса — семантические токены Earth Tech, без своей палитры:
//: `accent-brand` — «нужно внимание человека» (тот же тон, что у warning-alert).
const STATUS_TONE: Record<ImportRowStatus, string> = {
  matched: 'border-primary/40 bg-primary/10 text-foreground',
  needs_model: 'border-accent-brand/50 bg-accent-brand/10 text-foreground',
  duplicate: 'border-border bg-muted text-muted-foreground',
  error: 'border-destructive/40 bg-destructive/10 text-destructive',
};

function modelName(model: { name_translations: Record<string, string> }): string {
  return model.name_translations?.ru ?? Object.values(model.name_translations ?? {})[0] ?? '';
}

export function ImportMachineryDialog({
  open,
  farmId,
  farmName,
  onClose,
  onFinished,
}: {
  open: boolean;
  farmId: string;
  farmName: string;
  onClose: () => void;
  /** Загрузка что-то завела — карточке пора обновить парк и счётчики. */
  onFinished: () => void;
}) {
  const [step, setStep] = useState<Step>('file');
  const [file, setFile] = useState<File | null>(null);
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [filter, setFilter] = useState<ImportRowStatus | 'all'>('all');
  const [allowWithoutIds, setAllowWithoutIds] = useState(false);
  const [pickingFor, setPickingFor] = useState<string | null>(null);
  const [result, setResult] = useState<ApplyResult | null>(null);

  const preview = usePreviewParkImport(farmId);
  const apply = useApplyParkImport(farmId);

  const planned = plannedCount(rows, allowWithoutIds);
  const withoutIds = noIdsCount(rows);
  const counts = useMemo(() => {
    const acc: Record<ImportRowStatus, number> = { matched: 0, needs_model: 0, duplicate: 0, error: 0 };
    rows.forEach((row) => {
      acc[row.status] += 1;
    });
    return acc;
  }, [rows]);
  const shown = filter === 'all' ? rows : rows.filter((r) => r.status === filter);

  const reset = () => {
    setStep('file');
    setFile(null);
    setRows([]);
    setFilter('all');
    setAllowWithoutIds(false);
    setPickingFor(null);
    setResult(null);
  };

  const close = () => {
    reset();
    onClose();
  };

  const runPreview = async (picked: File) => {
    try {
      const data = await preview.mutateAsync(picked);
      setRows(data.rows);
      setStep('preview');
    } catch (err) {
      toast.error(importErrorText(err));
    }
  };

  const runApply = async () => {
    try {
      const outcome = await apply.mutateAsync({
        rows,
        fileName: file?.name ?? '',
        allowWithoutIds,
      });
      setResult(outcome);
      setStep('done');
      onFinished();
    } catch (err) {
      toast.error(importErrorText(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Загрузить парк техники · {farmName}
          </DialogTitle>
          <DialogDescription>
            {step === 'file' && 'Шаг 1 из 3 — файл. Пока не нажмёте «Применить», в парк ничего не запишется.'}
            {step === 'preview' && 'Шаг 2 из 3 — проверка. В парк ещё ничего не записано.'}
            {step === 'done' && 'Шаг 3 из 3 — готово.'}
          </DialogDescription>
        </DialogHeader>

        {step === 'file' && (
          <Stack gap={4}>
            <div className="rounded-md border border-dashed border-border bg-muted/30 p-4">
              <div className="flex flex-wrap items-center gap-3">
                <p className="flex-1 text-sm">
                  <span className="font-semibold">Образец «Парк техники»</span> — 7 колонок: марка / модель,
                  инвентарный номер, год выпуска, отделение, гос. номер, мощность (л.с.) и ширина захвата (м).
                  Обязательная одна — <span className="font-semibold">марка / модель</span>.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    void downloadParkTemplate().catch((err) => toast.error(importErrorText(err)));
                  }}
                  data-testid="machinery-import-template"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Скачать образец
                </Button>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                Первая строка — заголовки. Порядок колонок значения не имеет, лишние колонки не грузим:
                выгрузка из 1С подойдёт как есть.
              </p>
            </div>

            <div>
              <Label htmlFor="machinery-import-file">Ваш файл</Label>
              <input
                id="machinery-import-file"
                type="file"
                accept=".xlsx,.xlsm,.csv"
                className="mt-1 block w-full text-sm file:mr-3 file:rounded-md file:border file:border-border file:bg-muted file:px-3 file:py-2 file:text-sm"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                data-testid="machinery-import-input"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Excel (.xlsx, .xlsm) или .csv, до {IMPORT_MAX_MB} МБ и до {IMPORT_MAX_ROWS} строк.
              </p>
            </div>
          </Stack>
        )}

        {step === 'preview' && (
          <Stack gap={3}>
            <div className="rounded-md border border-border bg-card p-3 text-sm" data-testid="machinery-import-summary">
              <p className="font-semibold">
                {planned === 0 ? 'Заводить нечего.' : `Заведём ${planned}.`} Требуют выбора модели —{' '}
                {counts.needs_model}. Уже есть в парке — {counts.duplicate}. Ошибок — {counts.error}.
              </p>
              <p className="text-xs text-muted-foreground">
                Файл «{file?.name ?? ''}» · строк в файле {rows.length} · хозяйство «{farmName}»
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              {(['all', 'matched', 'needs_model', 'duplicate', 'error'] as const).map((key) => (
                <Button
                  key={key}
                  size="sm"
                  variant={filter === key ? 'default' : 'outline'}
                  onClick={() => setFilter(key)}
                >
                  {key === 'all' ? 'Все' : STATUS_TITLES[key]} ·{' '}
                  {key === 'all' ? rows.length : counts[key]}
                </Button>
              ))}
            </div>

            {pickingFor !== null && (
              <ModelPicker
                rawName={pickingFor}
                onCancel={() => setPickingFor(null)}
                onQueue={() => {
                  setRows((prev) => withChosenModel(prev, pickingFor, null));
                  setPickingFor(null);
                }}
                onPick={(model) => {
                  setRows((prev) => withChosenModel(prev, pickingFor, model));
                  setPickingFor(null);
                }}
              />
            )}

            <div className="rounded-md border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">Стр.</TableHead>
                    <TableHead>Из файла</TableHead>
                    <TableHead className="w-32">Инв. №</TableHead>
                    <TableHead>Что будет</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {shown.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-sm text-muted-foreground">
                        Таких строк в файле нет.
                      </TableCell>
                    </TableRow>
                  ) : (
                    shown.map((row) => (
                      <TableRow key={row.row_number} data-testid={`machinery-import-row-${row.row_number}`}>
                        <TableCell className="text-center text-sm">{row.row_number}</TableCell>
                        <TableCell className="text-sm">{row.name || '(пусто)'}</TableCell>
                        <TableCell className="text-sm">{row.inventory_number ?? '—'}</TableCell>
                        <TableCell className="text-sm">
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge variant="outline" className={cn('shrink-0', STATUS_TONE[row.status])}>
                              {STATUS_TITLES[row.status]}
                            </Badge>
                            {row.ref_name && (
                              <span>
                                модель <span className="font-semibold">{row.ref_name}</span>
                                {row.type_name ? ` (${row.type_name})` : ''}
                              </span>
                            )}
                            {(row.status === 'needs_model' || row.status === 'matched') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setPickingFor(row.name)}
                                data-testid={`machinery-import-pick-${row.row_number}`}
                              >
                                {row.status === 'needs_model' ? 'Выбрать модель' : 'Изменить'}
                              </Button>
                            )}
                          </div>
                          {row.matched_via === 'alias' && (
                            <p className="text-xs text-muted-foreground">
                              нашли по былому наименованию «{row.name}»
                            </p>
                          )}
                          {[...row.errors, ...row.warnings].map((text) => (
                            <p key={text} className="text-xs text-muted-foreground">
                              {text}
                            </p>
                          ))}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {withoutIds > 0 && (
              <label className="flex items-start gap-2 text-sm">
                <Checkbox
                  checked={allowWithoutIds}
                  onCheckedChange={(v) => setAllowWithoutIds(v === true)}
                  aria-label="Загрузить строки без инвентарного и гос. номера"
                  data-testid="machinery-import-allow-no-ids"
                />
                <span>
                  Загрузить их всё равно — {withoutIds} строк без инвентарного и гос. номера, повторная
                  загрузка задвоит.
                </span>
              </label>
            )}
            <p className="text-xs text-muted-foreground">
              Строку с ошибкой поправляют в файле и грузят заново — остальные строки её не ждут.
            </p>
          </Stack>
        )}

        {step === 'done' && result && (
          <div className="rounded-md border border-border bg-card p-3 text-sm" data-testid="machinery-import-done">
            {doneSummary(result, farmName).map((line, index) => (
              <p key={line} className={index === 0 ? 'font-semibold' : 'text-muted-foreground'}>
                {line}
              </p>
            ))}
            {result.queued > 0 && (
              <p className="mt-2 text-xs text-muted-foreground">
                Наименования из очереди разбора добавляют в каталог платформенно. Когда добавят —
                загрузите тот же файл ещё раз, строки подхватятся.
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          {step === 'file' && (
            <>
              <Button variant="ghost" onClick={close}>
                Отмена
              </Button>
              <Button
                disabled={!file || preview.isPending}
                onClick={() => file && void runPreview(file)}
                data-testid="machinery-import-check"
              >
                <Upload className="mr-2 h-4 w-4" />
                {preview.isPending ? 'Читаю файл…' : 'Проверить файл'}
              </Button>
            </>
          )}
          {step === 'preview' && (
            <>
              <Button
                variant="ghost"
                onClick={() => {
                  setStep('file');
                  setRows([]);
                }}
              >
                Назад
              </Button>
              <Button
                disabled={planned === 0 || apply.isPending}
                onClick={() => void runApply()}
                data-testid="machinery-import-apply"
              >
                {apply.isPending ? 'Завожу…' : applyButtonLabel(planned)}
              </Button>
            </>
          )}
          {step === 'done' && (
            <>
              <Button variant="ghost" onClick={reset}>
                Загрузить ещё файл
              </Button>
              <Button onClick={close}>Открыть парк техники</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Выбор модели прямо в строке проверки.
 *
 * Список — алфавит + поиск (канон проекта), без «часто выбираемых». Поиск
 * делает сервер: он ищет и по былым наименованиям, которых на клиенте нет.
 */
function ModelPicker({
  rawName,
  onPick,
  onQueue,
  onCancel,
}: {
  rawName: string;
  onPick: (model: { id: string; name: string; type_name: string | null }) => void;
  onQueue: () => void;
  onCancel: () => void;
}) {
  const [search, setSearch] = useState(rawName);
  const [draft, setDraft] = useTextFilterDraft(search, setSearch);
  const catalog = useMachineryCatalog(search, true);
  const models = catalog.data?.items ?? [];

  return (
    <div className="rounded-md border border-border bg-muted/30 p-3" data-testid="machinery-import-picker">
      <Label htmlFor="machinery-import-search">Из файла «{rawName}»</Label>
      <div className="relative mt-1">
        <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          id="machinery-import-search"
          className="pl-8"
          value={draft}
          placeholder="Поиск по названию, коду и былым наименованиям"
          onChange={(e) => setDraft(e.target.value)}
        />
      </div>
      <ScrollArea className="mt-2 h-40 rounded-md border border-border bg-background">
        {catalog.isLoading ? (
          <p className="p-3 text-sm text-muted-foreground">Гружу каталог…</p>
        ) : models.length === 0 ? (
          <p className="p-3 text-sm text-muted-foreground">
            В каталоге не нашлось. Оставьте в очереди разбора — наименование уйдёт в справочник на
            добавление, и повторная загрузка того же файла его подхватит.
          </p>
        ) : (
          <ul>
            {models.map((model) => (
              <li key={model.id}>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
                  onClick={() =>
                    onPick({ id: model.id, name: modelName(model), type_name: model.type_name })
                  }
                >
                  <span className="flex-1">{modelName(model)}</span>
                  {model.type_name && (
                    <span className="text-xs text-muted-foreground">{model.type_name}</span>
                  )}
                  <Check className="h-4 w-4 opacity-0" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </ScrollArea>
      <p className="mt-2 text-xs text-muted-foreground">
        Выбор применится ко всем строкам файла с таким же наименованием — одинаковые машины
        подтверждают один раз.
      </p>
      <div className="mt-2 flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Отмена
        </Button>
        <Button variant="outline" size="sm" onClick={onQueue} data-testid="machinery-import-queue">
          Оставить в очереди разбора
        </Button>
      </div>
    </div>
  );
}
